import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../api/axiosInstance';
import mammoth from 'mammoth';
import * as pdfjsLib from 'pdfjs-dist';
import {
  ArrowLeftIcon,
  PlusIcon,
  DocumentTextIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  TrashIcon,
  PencilIcon,
  EyeIcon,
  XMarkIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import logger from '../../../utils/logger';

// Configure PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const AIKnowledge = ({ showNotification }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [knowledge, setKnowledge] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all'); // all, document, faq, sop
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedKnowledge, setSelectedKnowledge] = useState(null);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [uploading, setUploading] = useState(false);

  const [newKnowledge, setNewKnowledge] = useState({
    title: '',
    content: '',
    contentType: 'text',
    category: 'general',
    tags: [],
    status: 'published',
    enabled: true,
    priority: 100
  });

  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchKnowledge();
  }, []);

  const fetchKnowledge = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/ai/knowledge');
      setKnowledge(response.data.data || []);
    } catch (error) {
      logger.error('Error fetching knowledge:', error);
      showNotification?.('Failed to load knowledge base', 'error');
    } finally {
      setLoading(false);
    }
  };

  const createKnowledge = async () => {
    try {
      setCreating(true);
      await axiosInstance.post('/api/ai/knowledge', newKnowledge);
      fetchKnowledge();
      setShowCreateModal(false);
      resetForm();
      showNotification?.('Knowledge entry created successfully', 'success');
    } catch (error) {
      logger.error('Error creating knowledge:', error);
      showNotification?.(error.response?.data?.message || 'Failed to create knowledge entry', 'error');
    } finally {
      setCreating(false);
    }
  };

  const updateKnowledge = async () => {
    try {
      setUpdating(true);
      await axiosInstance.put(`/api/ai/knowledge/${selectedKnowledge._id}`, newKnowledge);
      fetchKnowledge();
      setShowEditModal(false);
      setSelectedKnowledge(null);
      resetForm();
      showNotification?.('Knowledge entry updated successfully', 'success');
    } catch (error) {
      logger.error('Error updating knowledge:', error);
      showNotification?.(error.response?.data?.message || 'Failed to update knowledge entry', 'error');
    } finally {
      setUpdating(false);
    }
  };

  const deleteKnowledge = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      await axiosInstance.delete(`/api/ai/knowledge/${id}`);
      fetchKnowledge();
      showNotification?.('Knowledge item deleted', 'success');
    } catch (error) {
      logger.error('Error deleting knowledge:', error);
      showNotification?.('Failed to delete knowledge item', 'error');
    }
  };

  const openEditModal = (item) => {
    setSelectedKnowledge(item);
    setNewKnowledge({
      title: item.title,
      content: item.content,
      contentType: item.contentType,
      category: item.category || 'general',
      tags: item.tags || [],
      status: item.status || 'published',
      enabled: item.enabled !== undefined ? item.enabled : true,
      priority: item.priority || 100
    });
    setShowEditModal(true);
  };

  const openViewModal = (item) => {
    setSelectedKnowledge(item);
    setShowViewModal(true);
  };

  // Extract text from PDF file
  const extractTextFromPDF = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      let fullText = '';

      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(' ');
        fullText += pageText + '\n\n';
      }

      return fullText.trim();
    } catch (error) {
      logger.error('Error extracting PDF text:', error);
      throw new Error('Failed to extract text from PDF');
    }
  };

  // Extract text from DOCX file
  const extractTextFromDOCX = async (file) => {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const result = await mammoth.extractRawText({ arrayBuffer });
      return result.value;
    } catch (error) {
      logger.error('Error extracting DOCX text:', error);
      throw new Error('Failed to extract text from DOCX');
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

    // Validate file size (max 10MB for documents)
    if (file.size > 10 * 1024 * 1024) {
      showNotification?.('File size must be less than 10MB', 'error');
      event.target.value = '';
      return;
    }

    try {
      setUploading(true);
      let extractedContent = '';
      const fileName = file.name.replace(/\.[^/.]+$/, '');

      // Handle different file types
      if (fileExtension === '.pdf') {
        // Extract text from PDF
        showNotification?.('Extracting text from PDF...', 'info');
        extractedContent = await extractTextFromPDF(file);
      } else if (fileExtension === '.docx') {
        // Extract text from DOCX
        showNotification?.('Extracting text from DOCX...', 'info');
        extractedContent = await extractTextFromDOCX(file);
      } else if (fileExtension === '.doc') {
        // .doc format not supported
        showNotification?.('Legacy .doc format is not supported. Please save as .docx or copy-paste the content.', 'error');
        setUploading(false);
        event.target.value = '';
        return;
      } else {
        // Handle text-based files
        const textBasedTypes = ['.txt', '.md', '.markdown', '.html', '.htm', '.csv'];
        const isTextBased = textBasedTypes.includes(fileExtension) || file.type.includes('text');

        if (!isTextBased) {
          showNotification?.('Unsupported file type. Supported formats: .txt, .md, .html, .csv, .pdf, .docx', 'error');
          setUploading(false);
          event.target.value = '';
          return;
        }

        // Read as text
        extractedContent = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (e) => resolve(e.target.result);
          reader.onerror = () => reject(new Error('Failed to read file'));
          reader.readAsText(file);
        });
      }

      // Validate extracted content
      if (!extractedContent || extractedContent.trim().length === 0) {
        showNotification?.('No text content found in file', 'error');
        setUploading(false);
        event.target.value = '';
        return;
      }

      // Detect content type
      let contentType = 'document';
      if (fileName.toLowerCase().includes('sop') || fileName.toLowerCase().includes('procedure')) {
        contentType = 'sop';
      } else if (fileName.toLowerCase().includes('faq') || fileName.toLowerCase().includes('question')) {
        contentType = 'faq';
      } else if (fileName.toLowerCase().includes('policy')) {
        contentType = 'policy';
      }

      // Pre-fill the create modal with file data
      setNewKnowledge({
        title: fileName,
        content: extractedContent,
        contentType: contentType,
        category: 'general',
        tags: [],
        status: 'published',
        enabled: true,
        priority: 100
      });

      setShowCreateModal(true);
      showNotification?.(`File loaded as ${contentType}. Review and save.`, 'success');
      setUploading(false);
    } catch (error) {
      logger.error('Error uploading file:', error);
      showNotification?.(error.message || 'Failed to upload file', 'error');
      setUploading(false);
    }

    event.target.value = '';
  };

  const resetForm = () => {
    setNewKnowledge({
      title: '',
      content: '',
      contentType: 'text',
      category: 'general',
      tags: [],
      status: 'published',
      enabled: true,
      priority: 100
    });
    setNewTag('');
  };

  const addTag = () => {
    if (newTag && !newKnowledge.tags.includes(newTag.toLowerCase())) {
      setNewKnowledge({
        ...newKnowledge,
        tags: [...newKnowledge.tags, newTag.toLowerCase()]
      });
      setNewTag('');
    }
  };

  const removeTag = (tag) => {
    setNewKnowledge({
      ...newKnowledge,
      tags: newKnowledge.tags.filter(t => t !== tag)
    });
  };

  const getTypeIcon = (type) => {
    const icons = {
      document: '📄',
      faq: '❓',
      sop: '📋',
      policy: '📑',
      text: '📝',
      markdown: '📄'
    };
    return icons[type] || '📄';
  };

  const getTypeBadge = (type) => {
    const badges = {
      document: { label: 'Document', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
      faq: { label: 'FAQ', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
      sop: { label: 'SOP', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
      policy: { label: 'Policy', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
      text: { label: 'Text', color: 'bg-gray-100 text-gray-700 dark:bg-[#222222] dark:text-gray-300' },
      markdown: { label: 'Markdown', color: 'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300' }
    };
    return badges[type] || badges.document;
  };

  const filteredKnowledge = knowledge.filter(item => {
    const matchesSearch = item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         item.content?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesTab = activeTab === 'all' || item.contentType === activeTab;
    return matchesSearch && matchesTab;
  });

  const tabs = [
    { id: 'all', label: 'All', count: knowledge.length },
    { id: 'document', label: 'Documents', count: knowledge.filter(k => k.contentType === 'document').length },
    { id: 'faq', label: 'FAQs', count: knowledge.filter(k => k.contentType === 'faq').length },
    { id: 'sop', label: 'SOPs', count: knowledge.filter(k => k.contentType === 'sop').length }
  ];

  if (loading) {
    return (
      <div className="h-full bg-white dark:bg-[#111111] flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-gray-100"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-white dark:bg-[#111111] overflow-y-auto">
      {/* Header */}
      <div className="border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/ai-dashboard')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] rounded-lg transition-colors"
              >
                <ArrowLeftIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                  Knowledge Base
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  {knowledge.length} {knowledge.length === 1 ? 'item' : 'items'} • Documents, FAQs, and SOPs
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors disabled:opacity-50"
              >
                <ArrowUpTrayIcon className="w-4 h-4" />
                {uploading ? 'Uploading...' : 'Upload Document'}
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Create Entry
              </button>

              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileUpload}
                className="hidden"
              />
            </div>
          </div>

          {/* Search and Tabs */}
          <div className="mt-6 space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search knowledge base..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-100 focus:border-transparent"
              />
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-gray-200 dark:border-gray-800">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-gray-900 dark:border-white text-gray-900 dark:text-white'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {filteredKnowledge.length === 0 ? (
          <div className="text-center py-12">
            <DocumentTextIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              {searchQuery ? 'No results found' : 'No knowledge items yet'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {searchQuery
                ? 'Try adjusting your search query'
                : 'Upload documents, create FAQs, or add SOPs to help Iris AI answer questions'
              }
            </p>
            {!searchQuery && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#1a1a1a] text-gray-900 dark:text-white text-sm font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-[#222222] transition-colors"
                >
                  Upload Document
                </button>
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
                >
                  Create First Entry
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {filteredKnowledge.map((item) => {
              const badge = getTypeBadge(item.contentType);

              return (
                <div
                  key={item._id}
                  className="border border-gray-200 dark:border-gray-800 rounded-lg p-4 hover:border-gray-300 dark:hover:border-gray-700 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-8 h-8 rounded bg-gray-100 dark:bg-[#222222] flex items-center justify-center text-lg">
                          {getTypeIcon(item.contentType)}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                              {item.title}
                            </h3>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${badge.color}`}>
                              {badge.label}
                            </span>
                            {item.status === 'published' && (
                              <CheckCircleIcon className="w-4 h-4 text-green-500" />
                            )}
                          </div>
                          {item.content && (
                            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                              {item.content.substring(0, 150)}...
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 mt-2">
                        {item.category && <span>Category: {item.category}</span>}
                        {item.tags && item.tags.length > 0 && (
                          <div className="flex gap-1">
                            {item.tags.slice(0, 3).map((tag, idx) => (
                              <span key={idx} className="px-2 py-0.5 rounded bg-gray-100 dark:bg-[#222222] text-gray-700 dark:text-gray-300">
                                #{tag}
                              </span>
                            ))}
                            {item.tags.length > 3 && (
                              <span>+{item.tags.length - 3} more</span>
                            )}
                          </div>
                        )}
                        {item.createdAt && (
                          <span>Added: {new Date(item.createdAt).toLocaleDateString()}</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => openViewModal(item)}
                        className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => openEditModal(item)}
                        className="p-2 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => deleteKnowledge(item._id)}
                        className="p-2 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => !creating && setShowCreateModal(false)}
        >
          <div
            className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-[#1a1a1a] z-10">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Create Knowledge Entry
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Add documents, FAQs, SOPs, or policies to your knowledge base
                  </p>
                </div>
                <button
                  onClick={() => !creating && (setShowCreateModal(false), resetForm())}
                  disabled={creating}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newKnowledge.title}
                  onChange={(e) => setNewKnowledge({ ...newKnowledge, title: e.target.value })}
                  placeholder="e.g., How to reset password, Company Leave Policy"
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                />
              </div>

              {/* Type and Category */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Content Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={newKnowledge.contentType}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, contentType: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  >
                    <option value="text">Plain Text</option>
                    <option value="markdown">Markdown</option>
                    <option value="document">Document</option>
                    <option value="faq">FAQ</option>
                    <option value="sop">SOP</option>
                    <option value="policy">Policy</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={newKnowledge.category}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, category: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  >
                    <option value="general">General</option>
                    <option value="it">IT</option>
                    <option value="hr">HR</option>
                    <option value="finance">Finance</option>
                    <option value="sales">Sales</option>
                    <option value="support">Support</option>
                    <option value="operations">Operations</option>
                    <option value="security">Security</option>
                    <option value="compliance">Compliance</option>
                  </select>
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newKnowledge.content}
                  onChange={(e) => setNewKnowledge({ ...newKnowledge, content: e.target.value })}
                  placeholder="Enter the knowledge content here..."
                  rows={10}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm font-mono resize-none"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Tags (Optional)
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    placeholder="e.g., password, policy, urgent"
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100"
                  >
                    Add
                  </button>
                </div>
                {newKnowledge.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newKnowledge.tags.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-full flex items-center gap-2">
                        #{tag}
                        <button onClick={() => removeTag(tag)} className="text-blue-500 hover:text-red-600">
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-[#111111]/50 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between sticky bottom-0">
              <button
                onClick={() => {
                  if (!creating) {
                    setShowCreateModal(false);
                    resetForm();
                  }
                }}
                disabled={creating}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#1a1a1a] border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222] rounded-lg transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={createKnowledge}
                disabled={creating || !newKnowledge.title || !newKnowledge.content}
                className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-all disabled:opacity-50"
              >
                {creating && (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {creating ? 'Creating...' : 'Create Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal - Similar structure to Create Modal */}
      {showEditModal && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => !updating && setShowEditModal(false)}
        >
          <div
            className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-[#1a1a1a] z-10">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Edit Knowledge Entry
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                    Update your knowledge base entry
                  </p>
                </div>
                <button
                  onClick={() => !updating && (setShowEditModal(false), resetForm(), setSelectedKnowledge(null))}
                  disabled={updating}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Same fields as Create Modal */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={newKnowledge.title}
                  onChange={(e) => setNewKnowledge({ ...newKnowledge, title: e.target.value })}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Content Type
                  </label>
                  <select
                    value={newKnowledge.contentType}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, contentType: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  >
                    <option value="text">Plain Text</option>
                    <option value="markdown">Markdown</option>
                    <option value="document">Document</option>
                    <option value="faq">FAQ</option>
                    <option value="sop">SOP</option>
                    <option value="policy">Policy</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    Category
                  </label>
                  <select
                    value={newKnowledge.category}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, category: e.target.value })}
                    className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  >
                    <option value="general">General</option>
                    <option value="it">IT</option>
                    <option value="hr">HR</option>
                    <option value="finance">Finance</option>
                    <option value="sales">Sales</option>
                    <option value="support">Support</option>
                    <option value="operations">Operations</option>
                    <option value="security">Security</option>
                    <option value="compliance">Compliance</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={newKnowledge.content}
                  onChange={(e) => setNewKnowledge({ ...newKnowledge, content: e.target.value })}
                  rows={10}
                  className="w-full px-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm font-mono resize-none"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Tags
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addTag()}
                    placeholder="e.g., password, policy, urgent"
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white text-sm"
                  />
                  <button
                    onClick={addTag}
                    className="px-4 py-2 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-medium rounded-lg"
                  >
                    Add
                  </button>
                </div>
                {newKnowledge.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {newKnowledge.tags.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-full flex items-center gap-2">
                        #{tag}
                        <button onClick={() => removeTag(tag)} className="text-blue-500 hover:text-red-600">
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-[#111111]/50 border-t border-gray-200 dark:border-gray-800 flex items-center justify-between sticky bottom-0">
              <button
                onClick={() => {
                  if (!updating) {
                    setShowEditModal(false);
                    resetForm();
                    setSelectedKnowledge(null);
                  }
                }}
                disabled={updating}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#1a1a1a] border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222] rounded-lg transition-all disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={updateKnowledge}
                disabled={updating || !newKnowledge.title || !newKnowledge.content}
                className="flex items-center gap-2 px-6 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-sm font-semibold rounded-lg hover:bg-gray-800 dark:hover:bg-gray-100 transition-all disabled:opacity-50"
              >
                {updating && (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {updating ? 'Updating...' : 'Update Entry'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedKnowledge && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowViewModal(false)}
        >
          <div
            className="bg-white dark:bg-[#1a1a1a] rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-200 dark:border-gray-800 sticky top-0 bg-white dark:bg-[#1a1a1a] z-10">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                      {selectedKnowledge.title}
                    </h2>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getTypeBadge(selectedKnowledge.contentType).color}`}>
                      {getTypeBadge(selectedKnowledge.contentType).label}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Category: {selectedKnowledge.category} • Added {new Date(selectedKnowledge.createdAt).toLocaleDateString()}
                  </p>
                </div>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="px-6 py-5">
              <div className="prose dark:prose-invert max-w-none">
                <pre className="whitespace-pre-wrap text-sm text-gray-700 dark:text-gray-300 font-mono bg-gray-50 dark:bg-[#111111] p-4 rounded-lg">
                  {selectedKnowledge.content}
                </pre>
              </div>

              {selectedKnowledge.tags && selectedKnowledge.tags.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tags:</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedKnowledge.tags.map((tag, idx) => (
                      <span key={idx} className="px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-sm rounded-full">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 dark:bg-[#111111]/50 border-t border-gray-200 dark:border-gray-800 sticky bottom-0">
              <button
                onClick={() => setShowViewModal(false)}
                className="px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-[#1a1a1a] border border-gray-300 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-[#222222] rounded-lg transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIKnowledge;
